import { map } from 'lodash/fp';
import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { MessageType } from 'dashboard/lib/interfaces/Message';
import ApiResponseError from 'lib/errors/ApiResponseError';
import { rtkErrorHandlerLogic, UseRTKError } from 'lib/hooks/useRTKErrorHandler';

export type MutationResult = { isLoading: boolean; requestId?: string; error?: UseRTKError };

interface UseBatchMutationArgs {
  mutations: MutationResult[];
  onError: ({
    error,
    requestId,
  }: {
    error: ApiResponseError;
    requestId?: string;
  }) => { message: string; level?: MessageType } | undefined;
}

/**
 * A hook that takes in multiple RTK mutation hook results and manages overall loading state + error handling.
 * This is a separate hook from useBatchQuery since mutations don't differentiate between isLoading & isFetching.
 * https://redux-toolkit.js.org/rtk-query/usage/mutations#frequently-used-mutation-hook-return-values
 */
export default ({ mutations, onError }: UseBatchMutationArgs) => {
  const allIsLoadings = map('isLoading', Object.values(mutations));
  const allErrors = map('error', mutations);
  const allIds = map('requestId', mutations);

  const memoIsLoadings = useMemo(() => allIsLoadings, allIsLoadings);
  const memoErrors = useMemo(() => allErrors, allErrors);
  const memoIds = useMemo(() => allIds, allIds);

  const dispatch = useDispatch();

  useEffect(() => {
    memoErrors.forEach((error, i) => {
      rtkErrorHandlerLogic(error, dispatch, (error) => onError({ error, requestId: memoIds[i] }));
    });
  }, [memoErrors, memoIds]);

  return {
    isLoadingAll: memoIsLoadings.some(Boolean),
  };
};
